import React, { useState, useEffect, useCallback } from 'react';
import { API_CONFIG } from '../../config/api';

const PROVIDER_OPTIONS = [
  { value: 'groq',      label: 'Groq',      defaultModel: 'llama-3.3-70b-versatile' },
  { value: 'openai',    label: 'OpenAI',     defaultModel: 'gpt-4o' },
  { value: 'anthropic', label: 'Anthropic',  defaultModel: 'claude-sonnet-4-20250514' },
  { value: 'ollama',    label: 'Ollama',     defaultModel: 'llama-3.1-70b-versatile' },
];

const DARK = {
  surface: '#1e293b', border: '#334155',
  text: '#f1f5f9', textSecondary: '#94a3b8', textMuted: '#64748b',
  inputBg: '#0f172a', inputBorder: '#475569',
  successBg: '#064e3b', successText: '#6ee7b7',
  errorBg: '#7f1d1d', errorText: '#fca5a5',
  dangerBg: '#991b1b', dangerText: '#ffffff',
};
const LIGHT = {
  surface: '#ffffff', border: '#e2e8f0',
  text: '#0f172a', textSecondary: '#475569', textMuted: '#94a3b8',
  inputBg: '#f8fafc', inputBorder: '#cbd5e1',
  successBg: '#d1fae5', successText: '#065f46',
  errorBg: '#fee2e2', errorText: '#991b1b',
  dangerBg: '#dc2626', dangerText: '#ffffff',
};

const LLMSettings = ({ dark = true }) => {
  const t = dark ? DARK : LIGHT;
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }

  // Form state
  const [provider, setProvider] = useState('groq');
  const [model, setModel] = useState('llama-3.3-70b-versatile');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);

  const BASE = API_CONFIG.BASE_URL;

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BASE}/api/accuracy/llm-config`);
      const data = await res.json();
      if (data.success) setConfigs(data.data || []);
    } catch (err) {
      console.error('Failed to load LLM configs:', err);
    } finally {
      setLoading(false);
    }
  }, [BASE]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const handleProviderChange = (val) => {
    setProvider(val);
    const opt = PROVIDER_OPTIONS.find(p => p.value === val);
    if (opt) setModel(opt.defaultModel);
  };

  const handleSave = async () => {
    if (!provider || !model || !apiKey) {
      setMessage({ type: 'error', text: 'All fields are required' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${BASE}/api/accuracy/llm-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider, model, api_key: apiKey }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: `Config saved for ${provider}` });
        setApiKey('');
        setShowKey(false);
        fetchConfigs();
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to save' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, providerName) => {
    if (!window.confirm(`Delete config for "${providerName}"?`)) return;
    try {
      const res = await fetch(`${BASE}/api/accuracy/llm-config/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: `Deleted config for ${providerName}` });
        fetchConfigs();
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to delete' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const cardStyle = {
    background: t.surface, border: `1px solid ${t.border}`,
    borderRadius: 12, padding: 24, marginBottom: 20,
  };
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: t.textSecondary, marginBottom: 6 };
  const inputStyle = {
    width: '100%', padding: '10px 14px', fontSize: 14, color: t.text,
    background: t.inputBg, border: `1px solid ${t.inputBorder}`,
    borderRadius: 8, outline: 'none', boxSizing: 'border-box',
  };
  const btnPrimary = {
    padding: '10px 24px', fontSize: 14, fontWeight: 600, color: '#fff',
    background: '#2563eb', border: 'none', borderRadius: 8, cursor: 'pointer',
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px' }}>
      <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: t.text }}>
        LLM Provider Settings
      </h2>
      <p style={{ margin: '0 0 24px', fontSize: 14, color: t.textMuted }}>
        Configure API keys and models for LLM providers. Stored securely in the database.
      </p>

      {/* Status message */}
      {message && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14,
          background: message.type === 'success' ? t.successBg : t.errorBg,
          color: message.type === 'success' ? t.successText : t.errorText,
        }}>
          {message.text}
        </div>
      )}

      {/* Add / Update form */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: t.text }}>
          Add or Update Provider
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Provider</label>
            <select value={provider} onChange={e => handleProviderChange(e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer' }}>
              {PROVIDER_OPTIONS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Model</label>
            <input type="text" value={model} onChange={e => setModel(e.target.value)}
              placeholder="e.g. llama-3.3-70b-versatile" style={inputStyle} />
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>API Key</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showKey ? 'text' : 'password'}
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder="Paste your API key here"
              style={{ ...inputStyle, paddingRight: 80 }}
            />
            <button onClick={() => setShowKey(s => !s)}
              style={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                padding: '4px 12px', fontSize: 12, background: 'transparent',
                border: `1px solid ${t.inputBorder}`, borderRadius: 6,
                color: t.textMuted, cursor: 'pointer',
              }}>
              {showKey ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>
        <button onClick={handleSave} disabled={saving}
          style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Saving...' : 'Save Config'}
        </button>
      </div>

      {/* Existing configs */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: t.text }}>
          Saved Configurations
        </h3>
        {loading ? (
          <p style={{ color: t.textMuted, fontSize: 14 }}>Loading...</p>
        ) : configs.length === 0 ? (
          <p style={{ color: t.textMuted, fontSize: 14 }}>No configurations saved yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${t.border}` }}>
                {['Provider', 'Model', 'API Key', 'Status', 'Updated', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: t.textMuted, fontWeight: 600, fontSize: 12, textTransform: 'uppercase' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {configs.map(cfg => (
                <tr key={cfg.id} style={{ borderBottom: `1px solid ${t.border}` }}>
                  <td style={{ padding: '10px 12px', color: t.text, fontWeight: 600 }}>
                    {cfg.provider}
                  </td>
                  <td style={{ padding: '10px 12px', color: t.textSecondary, fontFamily: 'monospace', fontSize: 13 }}>
                    {cfg.model}
                  </td>
                  <td style={{ padding: '10px 12px', color: t.textMuted, fontFamily: 'monospace', fontSize: 13 }}>
                    {cfg.api_key_masked}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                      background: cfg.is_active ? t.successBg : t.errorBg,
                      color: cfg.is_active ? t.successText : t.errorText,
                    }}>
                      {cfg.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', color: t.textMuted, fontSize: 12 }}>
                    {cfg.updated_at ? new Date(cfg.updated_at).toLocaleDateString() : '-'}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <button onClick={() => handleDelete(cfg.id, cfg.provider)}
                      style={{
                        padding: '4px 12px', fontSize: 12, fontWeight: 600,
                        background: t.dangerBg, color: t.dangerText,
                        border: 'none', borderRadius: 6, cursor: 'pointer',
                      }}>
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default LLMSettings;
